// Copyright 2026 L3montree GmbH and the DevGuard Contributors.
// SPDX-License-Identifier: 	AGPL-3.0-or-later

import Orb from "./ui/orb";
import { Loader2 } from "lucide-react";
import React, { type FunctionComponent } from "react";
import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";

import { CheckCircleIcon, SparklesIcon } from "@heroicons/react/24/solid";
import { useActiveAsset } from "@/hooks/useActiveAsset";
import { Button } from "./ui/button";
import Link from "next/link";
import { toast } from "@/lib/toast";

interface Props {
  handleAutosetup: (pendingAutosetup: false) => Promise<void>;
  progress: {
    [key: string]: {
      status: "notStarted" | "pending" | "success";
      message: string;
      url?: string;
    };
  };
  Loader: () => React.ReactNode;
  isLoading: boolean;
}

const Autosetup: FunctionComponent<Props> = ({
  handleAutosetup,
  progress,
  isLoading,
  Loader,
}) => {
  const [timedOut, setTimedOut] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (isLoading) {
        toast.error(
          "The auto-setup is taking longer than expected. Please try again later.",
        );
        setTimedOut(true);
      }
    }, 300000);

    return () => clearTimeout(timer);
  }, [isLoading]);

  const isReallyLoading = isLoading && !timedOut;

  const asset = useActiveAsset();
  return (
    <div className="">
      <div className="relative isolate">
        <div className="mx-auto max-w-7xl">
          <div className="mx-auto flex flex-col gap-16 px-6 py-16 bg-card border sm:rounded-lg sm:p-8 lg:mx-0 lg:max-w-none lg:flex-row lg:items-center lg:py-20 xl:gap-x-20 xl:px-20">
            <div className="h-96 w-full flex-none max-w-sm relative">
              <span className="text-2xl font-semibold absolute inset-0 flex items-center justify-center">
                Auto-Setup
              </span>
              <Orb
                hoverIntensity={0.5}
                rotateOnHover={false}
                hue={0}
                forceHoverState={isReallyLoading}
              />
            </div>
            <div className="w-full flex-auto">
              <CardHeader>
                <CardTitle className="flex flex-row gap-2">
                  <SparklesIcon className="w-5 text-muted-foreground" /> Get
                  started in Seconds
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  You can use the auto-setup feature to automatically add the
                  DevGuard Pipeline to the GitLab/ openCode CI/CD pipeline of
                  your project
                  {asset?.repositoryName && (
                    <span> {asset?.repositoryName}</span>
                  )}
                  . This will create a Merge-Request and add any missing
                  configuration variables and webhooks.
                </CardDescription>
              </CardContent>
              <CardFooter className="flex flex-col items-start justify-start gap-4">
                {progress.mergeRequest.status === "success" &&
                progress.mergeRequest.url !== undefined ? (
                  <Link
                    href={
                      Object.values(progress).find(
                        (v) => v.url && v.status === "success",
                      )?.url || "#"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button disabled={isReallyLoading}>
                      <Loader />
                      View Merge Request
                    </Button>
                  </Link>
                ) : (
                  <Button
                    disabled={isReallyLoading}
                    onClick={() => handleAutosetup(false)}
                  >
                    <Loader />
                    Use Auto-Setup
                  </Button>
                )}
                <div className="flex flex-col gap-2">
                  {Object.entries(progress).map(([key, value], i) => (
                    <div
                      className="flex flex-row items-center gap-2 text-sm text-muted-foreground"
                      key={key}
                    >
                      {value.status === "notStarted" ? (
                        i + 1 + "."
                      ) : value.status === "pending" && !isReallyLoading ? (
                        <ExclamationCircleIcon className="mr-2 h-4 w-4 text-destructive" />
                      ) : value.status === "pending" && isReallyLoading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin " />
                      ) : (
                        <CheckCircleIcon className="h-4 w-4 text-success" />
                      )}
                      <span className="flex-1">
                        {value.message}
                        {"url" in value && (
                          <>
                            <br />
                            <a
                              href={value.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {value.url}
                            </a>
                          </>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
                {isReallyLoading && (
                  <p className="mt-4 text-xs text-muted-foreground">
                    Still setting up — this may take a few minutes. You can
                    safely leave this page open.
                  </p>
                )}
              </CardFooter>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Autosetup;
